import axios, { type AxiosRequestConfig, type AxiosInstance } from 'axios';

export const getApiUrl = (): string => {
  if (typeof window !== 'undefined' && !window.location.hostname.includes('localhost')) {
    return '/api';
  }
  return process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
};

const API_URL = getApiUrl();

interface ExtendedAxiosInstance extends AxiosInstance {
  backgroundRequest: (config: AxiosRequestConfig) => ReturnType<AxiosInstance>;
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ExtendedAxiosInstance;

let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];
let isRedirecting = false;
let hasEverAuthenticated = false;

const subscribeToRefresh = (callback: (success: boolean) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshComplete = (success: boolean) => {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
};

const attemptTokenRefresh = async (): Promise<boolean> => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeToRefresh(resolve);
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(`${API_URL}/auth/v2/refresh`, {}, { withCredentials: true });

    if (response.data.success) {
      onRefreshComplete(true);
      return true;
    }
  } catch (error) {
    console.error('Token refresh failed');
  } finally {
    isRefreshing = false;
  }

  onRefreshComplete(false);
  return false;
};

const handleAuthFailure = (returnUrl: string | null = null) => {
  if (isRedirecting) return;
  isRedirecting = true;

  let redirectUrl = '/login';
  if (returnUrl) {
    redirectUrl += `?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  window.dispatchEvent(new CustomEvent('auth:logout'));
  window.location.href = redirectUrl;
};

const handleForbidden = () => {
  if (isRedirecting) return;
  isRedirecting = true;

  window.dispatchEvent(new CustomEvent('auth:forbidden'));
  window.location.href = '/unauthorized';
};

api.interceptors.request.use(
  (config) => {
    config.headers['X-Request-ID'] = crypto.randomUUID();
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    isRedirecting = false;
    const url = response.config?.url || '';
    if (!url.includes('/auth/')) {
      hasEverAuthenticated = true;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 403) {
      console.warn('Access forbidden (403)');
      const isBackgroundRequest = originalRequest.headers?.['X-Background-Request'];
      if (!isBackgroundRequest) {
        handleForbidden();
      }
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const url = originalRequest.url || '';
      if (
        url.includes('/auth/login') ||
        url.includes('/auth/v2/login') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/v2/refresh') ||
        url.includes('/auth/v2/me') ||
        url.includes('/auth/me') ||
        url.includes('/chatbot/history')
      ) {
        return Promise.reject(error);
      }

      if (hasEverAuthenticated) {
        const refreshSuccess = await attemptTokenRefresh();

        if (refreshSuccess) {
          return api(originalRequest);
        }

        const currentPath = window.location.pathname + window.location.search;
        handleAuthFailure(currentPath);
      }

      return Promise.reject(error);
    }

    if (!error.response) {
      console.error('Network error:', error.message);
    }

    return Promise.reject(error);
  }
);

api.backgroundRequest = (config: AxiosRequestConfig) => {
  return api({
    ...config,
    headers: {
      ...config.headers,
      'X-Background-Request': 'true',
    },
  });
};

export default api;
